import {
  applyDeferred,
  carryDeferred,
  chipsContextOf,
  type FilterChip,
} from '../results/filter_chips_state.js';
import {
  defaultFilterState,
  priceBounds,
  type FlightFilterState,
} from '../results/filter_logic.js';
import type { FlightExplorerOffer } from './search_source.js';

/*
 * Os resets do filtro do explorer, puros: o que sobra do corte quando a busca
 * pousa por cima da semente e o filtro novo de uma lista que abre. Fora do
 * `explorer_state.ts` pelo teto de linhas dele.
 */

/**
 * O filtro sobrevive ao pouso da busca viva por cima da semente — e é o MESMO
 * motivo pelo qual `refine_ok` existe separado de `search_ok`: o corte que a
 * pessoa deu enquanto a semente estava em cena não pode morrer com a resposta que
 * ela mesma esperava. Semente e busca respondem ao MESMO pedido; um
 * `defaultFilterState` aqui apagaria "Sem escala" segundos depois do clique, sem
 * gesto de ninguém.
 *
 * Só o teto do slider rebaseia, e só quando está ENCOSTADO no teto da semente:
 * ali ele é o default da lista da semente e não uma decisão, e mantê-lo caparia a
 * lista viva num preço que ninguém escolheu. Abaixo do teto é corte deliberado e
 * fica. Mesma régua do rebase por facetas em `useServerFilter`.
 */
export function filterAfterRefresh(
  filter: FlightFilterState,
  seeded: FlightExplorerOffer[],
  offers: FlightExplorerOffer[],
): FlightFilterState {
  if (filter.maxPrice < priceBounds(seeded)[1]) return filter;
  return { ...filter, maxPrice: priceBounds(offers)[1] };
}

/** Filtro novo = chips zerados… EXCETO os `deferred` (R2-D3): um chip de outra perna descreve um
 *  corte que ainda vai existir. `carry.leg` é a perna que está abrindo — os deferred dela entram.
 *
 * Só aplica com `offers.length > 0`: a perna-alvo pode abrir ainda `loading` (`go_to_leg`/
 * `fares_picked` só rejeitam `idle`; `pick_removed` nem olha a fase) —
 * `offersOf` devolve `[]` para ela, e aplicar o deferred ali cunhava o corte sobre uma lista vazia
 * E tirava a marca `deferred` do chip. O próximo reset (o pouso REAL da busca, `search_ok`/
 * `refine_ok` firstFrame, que sempre passa `carry` com a perna que abriu) via `carryDeferred` só
 * mantém quem ainda está `deferred: true` — um chip já "aplicado" contra `[]` não seria mais
 * candidato e sumia de vez, junto com o corte que ele tinha acabado de cunhar. Manter o chip
 * `deferred` até a lista real chegar faz o primeiro quadro NÃO-VAZIO da perna ser quem aplica.
 *
 * `chipsContextOf(offers)` aqui é sempre a PÁGINA 1 da perna (o reducer não tem facetas do
 * servidor) — um `airlinesExclude`/`airportsExclude` deferred vira complemento de um universo
 * parcial, diferente do caminho vivo (`useFilterIntent`), que prefere as facetas quando há.
 */
export const resetFilter = (
  offers: FlightExplorerOffer[],
  ceiling?: number,
  carry?: { chips: FilterChip[]; leg: number },
) => {
  const base = {
    filter: defaultFilterState(offers, ceiling),
    chips: carry ? carryDeferred(carry.chips) : ([] as FilterChip[]),
  };
  return carry && offers.length > 0 ? applyDeferred(base, carry.leg, chipsContextOf(offers)) : base;
};
